// src/composables/usePwaInstall.ts
/**
 * Drives the "install this app" affordance for existing web users.
 *
 * Two platforms, two paths:
 *  - Android / desktop Chromium fire `beforeinstallprompt`. We capture and
 *    defer it, then replay it on a tap of our own button (browsers only allow
 *    prompt() from a user gesture). -> `canPrompt`
 *  - iOS Safari never fires that event and has no programmatic install, so we
 *    detect it and surface manual "Share -> Add to Home Screen" guidance.
 *    -> `showIosHint`
 *
 * State is module-scoped so the `beforeinstallprompt` listener is registered
 * once at import time (the event can fire before any component mounts) and is
 * shared by every caller.
 */
import { computed, ref } from 'vue'

const DISMISS_KEY = 'lp-install-dismissed'
// Re-offer install this long after the user dismisses the nudge.
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const isInstalled = ref(false)
const dismissedAt = ref<number>(readDismissedAt())

function readDismissedAt(): number {
  if (typeof localStorage === 'undefined') return 0
  const raw = localStorage.getItem(DISMISS_KEY)
  const n = raw ? Number(raw) : 0
  return Number.isFinite(n) ? n : 0
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: minimal-ui)').matches === true ||
    navigator.standalone === true
  )
}

function detectIos(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const iOSDevice = /iphone|ipad|ipod/i.test(ua)
  // iPadOS 13+ reports as desktop Safari but is touch + Mac platform.
  const iPadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1
  return iOSDevice || iPadOS
}

const isStandalone = ref(detectStandalone())
const isIos = detectIos()

// Register listeners once, at module load, so we never miss an early event.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: BeforeInstallPromptEvent) => {
    // Stop Chrome's default mini-infobar; we present our own affordance.
    e.preventDefault()
    deferredPrompt.value = e
  })

  window.addEventListener('appinstalled', () => {
    isInstalled.value = true
    isStandalone.value = true
    deferredPrompt.value = null
  })

  // Keep standalone state live if the user installs/launches mid-session.
  window.matchMedia?.('(display-mode: standalone)')?.addEventListener?.('change', (ev) => {
    if (ev.matches) isStandalone.value = true
  })
}

export function usePwaInstall() {
  const recentlyDismissed = computed(
    () => dismissedAt.value > 0 && Date.now() - dismissedAt.value < DISMISS_TTL_MS,
  )

  // Already an app (or freshly installed) -> nothing to offer.
  const isAppMode = computed(() => isStandalone.value || isInstalled.value)

  // Native install prompt is available and worth showing.
  const canPrompt = computed(
    () => !!deferredPrompt.value && !isAppMode.value && !recentlyDismissed.value,
  )

  // iOS: no programmatic prompt, so show manual instructions instead.
  const showIosHint = computed(
    () => isIos && !isAppMode.value && !recentlyDismissed.value,
  )

  /** Any install affordance available to show the user right now. */
  const canInstall = computed(() => canPrompt.value || showIosHint.value)

  /**
   * Fire the native install prompt. Returns the user's choice, or null when no
   * deferred prompt is available (e.g. iOS, or already handled).
   */
  async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
    const evt = deferredPrompt.value
    if (!evt) return null
    await evt.prompt()
    const { outcome } = await evt.userChoice
    // A deferred prompt can only be used once.
    deferredPrompt.value = null
    if (outcome === 'dismissed') dismiss()
    return outcome
  }

  /** Snooze the install nudge for DISMISS_TTL_MS. */
  function dismiss(): void {
    const now = Date.now()
    dismissedAt.value = now
    try {
      localStorage.setItem(DISMISS_KEY, String(now))
    } catch {
      /* private mode / storage full — non-fatal */
    }
  }

  return {
    canInstall,
    canPrompt,
    showIosHint,
    isIos,
    isStandalone: computed(() => isStandalone.value),
    isInstalled: computed(() => isInstalled.value),
    promptInstall,
    dismiss,
  }
}
